/* ============================================================
   SAM-198 — "Scheduling & Capacity" section, extracted out of
   ServiceEditorPage.tsx into its own file (SAM-546 field-engine-parity
   pass) so this section can be worked on in isolation from Details /
   Pricing / Policies / Booking engine, which other agents are touching
   concurrently in the same page.

   Field <-> engine-reader parity for everything in this section is
   documented next to `ENGINE_SCHEDULING_FIELD_PATHS` in
   src/hooks/useServiceEditor.ts — see that comment before adding or
   removing a field here.
   ============================================================ */
import React, { useMemo, useState } from 'react';
import { Clock, Calendar, Users } from 'lucide-react';
import {
  BOOKING_TYPE_OPTIONS, SUBTYPE_OPTIONS,
} from '../../../hooks/useServiceEditor';
import { EVENT_SIZE_BANDS, isEventSize, suggestEventSize } from '../../../utils/eventSize';
import { buildDayWindowsForSave } from '../../../utils/operatingWindows';
import { ServiceClosuresEditor } from '../../../components/ServiceClosuresEditor';
import { ServiceEffectivePreview } from '../../../components/ServiceEffectivePreview';
import { Field, Grid2, SectionCard, type Editor } from '../ServiceEditorPage';
import { useBusinessById } from '../../../hooks/useBusinessCrud';
import type { BusinessSchedule } from '../../../utils/businessHours';
import { businessScheduleToOperatingHours } from '../../../utils/serviceHoursSource';
import { BusinessHoursReadOnly, HoursSourcePicker } from './HoursSourcePicker';
import { BookingWindowSentence, DayHoursEditor, WeekBarsChart } from './BookingHoursParts';
import { capacityFromResources, copyDayToAll, resourceWord } from '../editorV2';

function CalendarOffIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4.18 4.18A2 2 0 0 0 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 1.82-1.18M21 15.5V6a2 2 0 0 0-2-2H9.5M16 2v4M8 2v4M2 10h4M2 2l20 20" />
    </svg>
  );
}


// Only stays, multi-day events and custom services span a day range. A cabana, day pass or
// minimum-consumption entry is always one date, so Min/Max Days would only confuse the admin.
const SPANS_DAYS = new Set<string>(['', 'stay', 'multiday', 'equipment']);

/** SAM-198 editor v2 — "Booking & hours" (mock 3 v2): the booking window as a sentence, capacity
 * read from Resources, opening hours (same as the business / custom per day) with the week in
 * bars, closures. Same form fields and payload as before. */
export function SchedulingSection({ editor, onGoResources }: { editor: Editor; onGoResources?: () => void }) {
  const {
    formData, setFormData, updateField, updateOperatingHour, addExtraWindow, removeExtraWindow, updateExtraWindow,
    operatingHourErrors, addClosure, removeClosure, updateClosure, isEventForm,
  } = editor;

  // SAM-592 — Event size: suggested from max capacity, editable. '' (never chosen) shows the suggestion,
  // which is also what the save payload persists.
  const suggestedEventSize = suggestEventSize(formData.max_capacity);
  const effectiveEventSize = isEventSize(formData.event_size) ? formData.event_size : suggestedEventSize ?? '';

  // SAM-656 — the business's Schedule, read only when the service follows it.
  const followsBusiness = formData.hours_source === 'business';
  // Also read for custom hours: the weekly bars show the business's hours behind the service's.
  const { data: businessRows, isLoading: businessLoading } = useBusinessById(formData.business_id || null);
  const business = (businessRows?.[0] ?? null) as BusinessSchedule | null;

  const ownOperatingHours = useMemo(() => Object.fromEntries(
    Object.entries(formData.operating_hours)
      .filter(([, v]) => v.enabled && v.open && v.close)
      .map(([k, v]) => {
        const { first, extras } = buildDayWindowsForSave({ open: v.open, close: v.close }, v.extra_windows);
        return [k, { open: first.open, close: first.close, ...(extras.length ? { extra_windows: extras } : {}) }];
      })
  ), [formData.operating_hours]);
  const businessOperatingHours = useMemo(() => businessScheduleToOperatingHours(business), [business]);
  const effectiveOperatingHours = useMemo(
    () => (followsBusiness ? businessOperatingHours : ownOperatingHours),
    [followsBusiness, businessOperatingHours, ownOperatingHours],
  );

  // Capacity is read from Resources when the service has them (mock 3 v2); the venue limit stays
  // editable behind a toggle so an existing max_capacity is never hidden from the admin.
  const fromResources = capacityFromResources(formData.resources);
  const word = resourceWord(formData.subtype);
  const [showLimit, setShowLimit] = useState(() => formData.max_capacity.trim() !== '');

  const toggleDay = (day: string, on: boolean) => {
    setFormData((prev) => {
      const d = prev.operating_hours[day];
      return {
        ...prev,
        operating_hours: {
          ...prev.operating_hours,
          [day]: on && !d.open && !d.close ? { ...d, enabled: true, open: '09:00', close: '18:00' } : { ...d, enabled: on },
        },
      };
    });
  };
  // Removing the first range promotes the next one; with nothing left the day closes.
  const removeFirstRange = (day: string) => {
    setFormData((prev) => {
      const d = prev.operating_hours[day];
      const [next, ...rest] = d.extra_windows;
      const updated = next
        ? { ...d, open: next.open, close: next.close, extra_windows: rest }
        : { ...d, enabled: false };
      return { ...prev, operating_hours: { ...prev.operating_hours, [day]: updated } };
    });
  };
  const copyMonday = () => setFormData((prev) => ({ ...prev, operating_hours: copyDayToAll(prev.operating_hours, 'monday') }));

  // Prototype's `schedulingType()`/`SCHED_NOTE` (secAvail() in services-flow.js) — the scheduling
  // type shown as a read-only chip once an Offering Type has derived it, same wording. The
  // create-flow agent is the one that sets `subtype` (and, through it, `booking_type`); this
  // section only renders the result. 'flexible' wording matches the prototype's own
  // SCHED_NOTE.flexible (services-flow.js) verbatim — SAM-198 follow-up (PR #420)
  // widened the backend subtype vocabulary to include `equipment`, whose default
  // scheduling type is 'flexible' (see SERVICE_SUBTYPE_SCHEDULING_TYPE).
  const SCHED_NOTE: Record<'day' | 'fixed' | 'flexible', string> = {
    day: 'Guests pick a date, never a time. Capacity is counted per day.',
    fixed: 'Set duration — guests pick a start time.',
    flexible: 'Variable duration between a minimum and a maximum.',
  };

  return (
    <>
      <SectionCard title="How it’s booked" icon={<Clock size={14} />}>
        {formData.subtype && (
          <div
            className="inline-flex items-center gap-1.5 text-[12px] rounded-8 px-2.5 py-1.5 mb-3.5"
            style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', color: 'var(--ink-700)' }}
          >
            <code className="font-semibold" style={{ color: 'var(--ink-900)' }}>{formData.booking_type}</code>
            <span style={{ color: 'var(--ink-400)' }}>·</span>
            <span>{SCHED_NOTE[formData.booking_type]}</span>
          </div>
        )}
        <Field
          label="Booking Type"
          caption={
            formData.subtype
              ? `Set by the ${SUBTYPE_OPTIONS.find((o) => o.id === formData.subtype)?.label} setup.`
              : formData.booking_type === 'day'
                ? 'Guests pick a date — no time-of-day selector.'
                : formData.booking_type === 'flexible'
                  ? 'Variable duration between a minimum and a maximum — no dedicated min/max UI yet.'
                  : 'Guests pick a time slot within Operating Hours.'
          }
        >
          <select
            value={formData.booking_type}
            onChange={(e) => updateField('booking_type', e.target.value as 'fixed' | 'day' | 'flexible')}
            disabled={!!formData.subtype}
          >
            {BOOKING_TYPE_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </Field>

        {formData.booking_type === 'fixed' && (
          <>
            <Grid2>
              <Field label="Slot Duration (min)" caption="How long each booking lasts.">
                <input
                  type="number"
                  value={formData.interval_duration}
                  onChange={(e) => updateField('interval_duration', e.target.value)}
                  placeholder="60"
                />
              </Field>
              <Field label="Max Per Slot" caption="How many bookings can share the same slot.">
                <input
                  type="number"
                  value={formData.max_per_slot}
                  onChange={(e) => updateField('max_per_slot', e.target.value)}
                  placeholder="1"
                />
              </Field>
            </Grid2>
            <Grid2>
              <Field label="Start Padding (min)" caption="Preparation time blocked before each slot.">
                <input
                  type="number"
                  value={formData.start_padding}
                  onChange={(e) => updateField('start_padding', e.target.value)}
                  placeholder="0"
                />
              </Field>
              <Field label="End Padding (min)" caption="Cleanup time blocked after each slot.">
                <input
                  type="number"
                  value={formData.end_padding}
                  onChange={(e) => updateField('end_padding', e.target.value)}
                  placeholder="0"
                />
              </Field>
            </Grid2>
            <Field
              label="Bookable Interval (min)"
              caption="Optional. How often a new start time repeats within the slot (e.g. a 60-min slot offered every 15 min). Leave blank to offer one start time per Slot Duration — engine-only field, has no local effect."
            >
              <input
                type="number"
                value={formData.bookable_interval}
                onChange={(e) => updateField('bookable_interval', e.target.value)}
                placeholder="e.g. 15 — defaults to Slot Duration"
              />
            </Field>
          </>
        )}

        {formData.booking_type === 'day' && SPANS_DAYS.has(formData.subtype) && (
          <Grid2>
            <Field label="Min Days" caption="Shortest stay a guest can book. Leave blank for no minimum.">
              <input
                type="number"
                min={0}
                value={formData.min_days}
                onChange={(e) => updateField('min_days', e.target.value)}
                placeholder="e.g. 1"
              />
            </Field>
            <Field label="Max Days" caption="Longest stay a guest can book. Leave blank for no maximum.">
              <input
                type="number"
                min={0}
                value={formData.max_days}
                onChange={(e) => updateField('max_days', e.target.value)}
                placeholder="e.g. 14"
              />
            </Field>
          </Grid2>
        )}

      </SectionCard>

      <SectionCard title="When can guests book?" icon={<Calendar size={14} />}>
        <BookingWindowSentence
          aheadDays={formData.booking_window_end}
          leadHours={formData.booking_window_start}
          onAheadDays={(v) => updateField('booking_window_end', v)}
          onLeadHours={(v) => updateField('booking_window_start', v)}
          perTime={formData.booking_type !== 'day'}
        />
      </SectionCard>

      <SectionCard title={formData.booking_type === 'day' ? 'Capacity per day' : 'Capacity'} icon={<Users size={14} />}>
        {fromResources.count > 0 ? (
          <div className="flex items-center gap-3 flex-wrap mb-3" data-testid="capacity-from-resources">
            <p className="text-[14px] flex-1 min-w-[200px]" style={{ color: 'var(--ink-900)' }}>
              {fromResources.count} {fromResources.count === 1 ? word.singular : word.plural}, each booked once{' '}
              {formData.booking_type === 'day' ? 'a day' : 'per time slot'} · up to {fromResources.guests} guests in total
            </p>
            {onGoResources && (
              <button type="button" className="btn ghost" onClick={onGoResources}>Edit resources</button>
            )}
          </div>
        ) : (
          <p className="text-[12px] mb-3" style={{ color: 'var(--ink-500)' }}>
            1 pool (no named resources): the limit below is the whole capacity.
          </p>
        )}
        {fromResources.count > 0 && !showLimit ? (
          <button
            type="button" onClick={() => setShowLimit(true)}
            className="text-[12px] font-semibold underline border-0 bg-transparent p-0 cursor-pointer"
            style={{ color: 'var(--tenant-primary, #005488)' }}
          >
            Set a total guest limit
          </button>
        ) : (
          <Field
            label={formData.booking_type === 'day' ? 'Total guests per day' : 'Total guests at once'}
            caption={
              formData.booking_type === 'day'
                ? 'Maximum guests across all bookings for one date.'
                : 'Maximum guests this service can hold at once (venue aforo).'
            }
          >
            <input
              type="number"
              value={formData.max_capacity}
              onChange={(e) => updateField('max_capacity', e.target.value)}
              placeholder="e.g. 20"
            />
          </Field>
        )}

        {isEventForm && (
          <Field
            label="Event size"
            caption={
              suggestedEventSize
                ? `Events are charged by size, not per ticket. Suggested from the guest limit (${formData.max_capacity}): ${suggestedEventSize.toUpperCase()}. The booking price uses the actual guest count.`
                : 'Events are charged by size, not per ticket. Set the total guest limit to get a suggestion.'
            }
          >
            <select
              value={effectiveEventSize}
              onChange={(e) => updateField('event_size', isEventSize(e.target.value) ? e.target.value : '')}
            >
              <option value="">Select a size…</option>
              {EVENT_SIZE_BANDS.map((b) => (
                <option key={b.key} value={b.key}>
                  {b.key.toUpperCase()} — {b.label}{b.key === suggestedEventSize ? ' (suggested)' : ''}
                </option>
              ))}
            </select>
          </Field>
        )}
      </SectionCard>

      {/* Operating hours + effective-schedule preview side by side — the drawer stacked these
          vertically because a 640px panel had no room for two columns; the full page does. */}
      <SectionCard title="Opening days and hours" icon={<Calendar size={14} />}>
        <HoursSourcePicker
          value={formData.hours_source}
          onChange={(value) => updateField('hours_source', value)}
        />

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_280px] gap-5">
          <div className="min-w-0">
            {followsBusiness ? (
              <BusinessHoursReadOnly
                businessId={formData.business_id}
                business={business}
                businessLoading={!!formData.business_id && businessLoading}
              />
            ) : (
              <DayHoursEditor
                hours={formData.operating_hours}
                errors={operatingHourErrors}
                onToggle={toggleDay}
                onFirstChange={(day, field, value) => updateOperatingHour(day, field, value)}
                onRemoveFirst={removeFirstRange}
                onAdd={addExtraWindow}
                onExtraChange={(day, index, field, value) => updateExtraWindow(day, index, field, value)}
                onRemoveExtra={removeExtraWindow}
                onCopyMonday={copyMonday}
              />
            )}
          </div>

          <div className="min-w-0 flex flex-col gap-3">
            <WeekBarsChart
              hours={effectiveOperatingHours}
              businessHours={followsBusiness || !business ? undefined : businessOperatingHours}
            />
            <ServiceEffectivePreview
              operatingHours={effectiveOperatingHours}
              closures={formData.closures}
              resources={formData.resources}
              bookingType={formData.booking_type}
              maxCapacity={formData.max_capacity}
              maxPerSlot={formData.max_per_slot}
              dailyCapacity={formData.max_capacity}
              showSchedule={false}
            />
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title={`Closures${formData.closures.length ? ` (${formData.closures.length})` : ''}`}
        icon={<CalendarOffIcon />}
      >
        <ServiceClosuresEditor
          closures={formData.closures}
          onAdd={addClosure}
          onRemove={removeClosure}
          onChange={updateClosure}
        />
      </SectionCard>
    </>
  );
}
